import { useCallback, useMemo } from 'react';
import {
  useQueryClient,
  useMutation,
  useQuery,
  // UseMutateFunction,
} from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { RegisterAccountPayload } from '../../api/account';
import {
  register,
  signIn,
  getAccount,
  forgotPassword,
  getAccountsList,
  resetPassword,
  addUserToAccount,
  updateAnimatorAccount,
  getUserBadges,
  requestEditLink,
  getEditRegistration,
  updateRegistrationByToken,
  requestBadgeByEmail,
} from '../../api/account';
import { IUser } from '~/types/users';
import { IAccount, ArgsGetAccountList } from '~/types/accounts';

const useSignIn = () => useMutation<any, unknown, {
  email: string, password: string
}, unknown>(({
  email,
  password,
}) => signIn(email, password));

const useCreateAccount = () => useMutation<any, unknown, RegisterAccountPayload, unknown>(
  (data) => register(data),
);

const useForgotPassword = () => useMutation<any, unknown, {
  email: string
}, unknown>(({
  email,
}) => forgotPassword(email));

const useRequestEditLink = () => useMutation<any, unknown, {
  email: string
}, unknown>(({
  email,
}) => requestEditLink(email));

const useRequestBadgeByEmail = () => useMutation<any, unknown, {
  email: string
}, unknown>(({
  email,
}) => requestBadgeByEmail({ email }));

const useResetPassword = () => useMutation<any, unknown, {
  password: string,
  token: string
}, unknown>(({
  password,
  token,
}) => resetPassword(password, token));

const useEditRegistration = (token: string) => useQuery(
  ['edit-registration', token],
  async () => getEditRegistration(token),
  {
    enabled: !!token,
  },
);

const useUpdateRegistrationByToken = () => {
  const queryClient = useQueryClient();
  return useMutation<any, unknown, {
    token: string,
    payload: any,
  }, unknown>(({
    token,
    payload,
  }) => updateRegistrationByToken(token, payload), {
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['account-users'] });
    },
  });
};

const useSignOut = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const onSignOut = useCallback(() => {
    queryClient.setQueryData(['account'], null);
    localStorage.removeItem('token');
    queryClient.removeQueries();
    navigate('/login');
  }, [navigate, queryClient]);

  return onSignOut;
};

const useAccount = () => useQuery(
  ['account'],
  async (): Promise<{ account: IAccount } | null> => getAccount(),
);

const useAccountBadges = (userId: string) => useQuery(
  ['account-users', userId],
  async (): Promise<{ users: any; companyType?: string } | null> => getUserBadges(userId),
  {
    enabled: !!userId,
  },
);

const useAccountsList = (params?: ArgsGetAccountList) => {
  const query = useQuery({
    queryKey: ['account-list', params],
    queryFn: () => getAccountsList(params),
  });

  const transformedData = useMemo(() => query.data?.list || {}, [query.data]);

  return {
    ...query,
    data: transformedData,
  };
};

const useAddUserToAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // eslint-disable-next-line max-len
    mutationFn: (data: { accountId: string, userData: Partial<IUser> }) => addUserToAccount(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['account'] });
    },
  });
};

const useUpdateAnimatorAccount = (successMessage?: string) => {
  const notify = () => toast(successMessage || '');
  const queryClient = useQueryClient();
  return useMutation({
    // eslint-disable-next-line max-len
    mutationFn: (data: {
      region: string,
      userFunction: string,
      presences: Pick<IUser, 'presences'>
    }) => updateAnimatorAccount(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['account'] });
      if (successMessage) notify();
    },
  });
};

export {
  useSignIn,
  useCreateAccount,
  useForgotPassword,
  useRequestEditLink,
  useRequestBadgeByEmail,
  useResetPassword,
  useEditRegistration,
  useUpdateRegistrationByToken,
  useSignOut,
  useAccount,
  useAccountsList,
  useAddUserToAccount,
  useUpdateAnimatorAccount,
  useAccountBadges,
};
